// istanbul ignore file
import React, { useEffect } from 'react'
import { Container, Grid } from '@mui/material'
import StageForm from './StageForm'
import { type Stage } from '../create-custom-kpi/types'
import { type Option } from '../../../../components/GenXDropdown'
import { useDispatch, useSelector } from 'react-redux'
import {
  setStages
} from '../../redux/customKpiSlice'
import { type RootState } from '../../../../store'
import { type Asset } from '../../services/assetListService'

interface LabelValue {
  label: string
  value: string
  typeId?: string
}
interface CreateKpiTabProps {
  assetList: Asset[]
  kpiType: string
  deviceType: string
  selectedAsset: LabelValue | null
  currentKpiId?: string
}

const CreateKpiTab: React.FC<CreateKpiTabProps> = (props) => {
  const kpiStages = useSelector((state: RootState) => state.customKpi)
  const customKpiDetails = useSelector((state: RootState) => state.kpiDetails)
  const dispatch = useDispatch()

  const operators: Option[] = [
    { value: 'ARITHMETIC', label: 'Arithmetic' },
    { value: 'LOGICAL', label: 'Logical' }
  ]
  const logicalIndicators: Option[] = [
    { value: 'AND', label: '&' },
    { value: 'OR', label: '|' },
    { value: 'LESS_THAN', label: '<' },
    { value: 'GREATER_THAN', label: '>' },
    { value: 'EQUAL', label: '=' },
    { value: 'NOT_EQUAL', label: '!=' },
    { value: 'LESS_OR_EQUAL', label: '<=' },
    { value: 'GREATER_OR_EQUAL', label: '>=' }
  ]
  const arithmeticIndicators: Option[] = [
    { value: 'ADD', label: '+' },
    { value: 'SUBTRACT', label: '-' },
    { value: 'MULTIPLY', label: '*' },
    { value: 'DIVIDE', label: '/' },
    { value: 'POWER', label: '^' },
    { value: 'MIN', label: 'min' },
    { value: 'MAX', label: 'max' },
    { value: 'AVG', label: 'avg' }
  ]

  useEffect(() => {
    if (customKpiDetails.kpi != null) {
      const isTotalizer = kpiStages.totalizer
      const isTrueCondition = kpiStages.trueCondition
      const updatedStages = kpiStages.stages.map((stage, index) => {
        const customStage = customKpiDetails.kpi?.stage[index]
        if (customStage != null && index === 0) {
          return {
            parameter1: {
              assetId: customStage.kpi_parameters[0]?.asset_id,
              asset: customStage.kpi_parameters[0]?.asset_name,
              deviceId: customStage.kpi_parameters[0]?.device_id,
              device: customStage.kpi_parameters[0]?.device_name,
              parameterId: (isTotalizer && !isTrueCondition) ? '' : customStage.kpi_parameters[0]?.parameter_id,
              parameterType: (isTotalizer && !isTrueCondition) ? '' : customStage.kpi_parameters[0]?.parameter_type,
              parameter: (isTotalizer && !isTrueCondition) ? '' : customStage.kpi_parameters[0]?.parameter_name,
              constantValue: customStage.kpi_parameters[0]?.constant_value?.toString()
            },
            parameter2: {
              assetId: customStage.kpi_parameters[1]?.asset_id,
              asset: customStage.kpi_parameters[1]?.asset_name,
              deviceId: customStage.kpi_parameters[1]?.device_id,
              device: customStage.kpi_parameters[1]?.device_name,
              parameterId: customStage.kpi_parameters[1]?.parameter_id,
              parameterType: customStage.kpi_parameters[1]?.parameter_type,
              parameter: customStage.kpi_parameters[1]?.parameter_name,
              constantValue: customStage.kpi_parameters[1]?.constant_value?.toString()
            },
            arithmeticIndicator: {
              value: isTotalizer ? '' : customStage.operator_type === 'ARITHMETIC' ? arithmeticIndicators.find(op => op.value === customStage.operator)?.value : logicalIndicators.find(op => op.value === customStage.operator)?.value,
              label: isTotalizer ? '' : customStage.operator_type === 'ARITHMETIC' ? arithmeticIndicators.find(op => op.value === customStage.operator)?.label : logicalIndicators.find(op => op.value === customStage.operator)?.label
            },
            calculatedName: customStage.calculated_name,
            operator: isTotalizer ? '' : customStage.operator,
            operatorType: isTotalizer ? '' : customStage.operator_type
          }
        } else if (customStage != null) {
          return {
            parameter1: {
              assetId: '',
              asset: '',
              deviceId: '',
              device: '',
              parameterId: '',
              parameter: ''
            },
            parameter2: {
              assetId: customStage.kpi_parameters[0]?.asset_id,
              asset: customStage.kpi_parameters[0]?.asset_name,
              deviceId: customStage.kpi_parameters[0]?.device_id,
              device: customStage.kpi_parameters[0]?.device_name,
              parameterId: customStage.kpi_parameters[0]?.parameter_id,
              parameterType: customStage.kpi_parameters[0]?.parameter_type,
              parameter: customStage.kpi_parameters[0]?.parameter_name,
              constantValue: customStage.kpi_parameters[0]?.constant_value?.toString()
            },
            arithmeticIndicator: {
              value: customStage.operator_type === 'ARITHMETIC' ? arithmeticIndicators.find(op => op.value === customStage.operator)?.value : logicalIndicators.find(op => op.value === customStage.operator)?.value,
              label: customStage.operator_type === 'ARITHMETIC' ? arithmeticIndicators.find(op => op.value === customStage.operator)?.label : logicalIndicators.find(op => op.value === customStage.operator)?.label
            },
            calculatedName: customStage.calculated_name,
            operator: customStage.operator,
            operatorType: customStage.operator_type
          }
        }
        return stage
      })
      dispatch(setStages(updatedStages))
    }
  }, [customKpiDetails.kpi])

  // Transform assetList
  // istanbul ignore next
  const assets = props.assetList.map((device: any) => ({
    id: device.id,
    name: device.name,
    deviceTypeId: device.deviceTypeId
  }))
  const assetsArr = (props.selectedAsset != null) ? [{ id: props.selectedAsset.value, name: props.selectedAsset.label, deviceTypeId: props.selectedAsset.typeId }, ...assets] : assets

  // Function to handle changes in stage fields
  /* istanbul ignore next */
  const handleStageChange = (stageIndex: number, field: keyof Stage, value: any, asset?: Option | null, device?: Option | null, constantValue?: string): void => {
    try {
      if (field === 'parameter1' || field === 'parameter2') {
        const parameter = {
          assetId: asset?.value ?? '',
          asset: asset?.label ?? '',
          deviceId: device?.value ?? '',
          device: device?.label ?? '',
          parameterId: value.value,
          parameter: value.label,
          parameterType: value.type,
          constantValue: constantValue ?? ''
        }
        value = parameter
      }
      const updatedStages = kpiStages.stages.map((kpiStages, index) =>
        index === stageIndex ? { ...kpiStages, [field]: value } : kpiStages
      )
      console.log('parameter', updatedStages)
      dispatch(setStages(updatedStages))
    } catch (error) {
      console.error('Error occurred while updating stage')
    }
  }

  // istanbul ignore next
  return (
    <Grid sx={{ maxHeight: '80vh' }} data-testid='create-kpi-tab'>
      <Container maxWidth={false} sx={{ display: 'flex', paddingLeft: '16px !important', flexDirection: 'row', overflowX: 'auto' }}>
        {kpiStages.stages.map((stage, index) => (
          <StageForm
            key={index}
            data-testid={`stage-form-${index}`}
            index={index}
            stages={kpiStages.stages}
            isTotalizer={kpiStages.totalizer}
            assetList={assetsArr}
            arithmeticIndicators={arithmeticIndicators}
            logicalIndicators={logicalIndicators}
            operators={operators}
            operatorType={kpiStages.stages[index]?.operatorType === '' ? '' : kpiStages.stages[index]?.operatorType === 'LOGICAL' ? 'Logical Indicator' : 'Arithmetic Indicator'}
            handleStageChange={handleStageChange}
            kpiType={props.kpiType}
            deviceType={props.deviceType}
            currentKpiId={props.currentKpiId}
          />
        ))}
      </Container>
    </Grid>
  )
}

export default CreateKpiTab
